"use client";

import { SignIn, SignUp, useUser } from "@clerk/nextjs";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { Logo } from "@/components/assets/icon/logo";
import { cn } from "@/lib/_utils";
import { LOGIN_SLIDES } from "./data";

type Role = "diyer" | "tradie";
type AuthMode = "sign-in" | "sign-up";

type HomeV2LoginProps = {
  initialMode?: AuthMode;
};

const clerkAppearance = {
  layout: {
    socialButtonsPlacement: "bottom" as const,
    socialButtonsVariant: "iconButton" as const,
  },
  elements: {
    rootBox: "w-full",
    cardBox: "w-full border-none shadow-none bg-transparent",
    card: "w-full border-none bg-transparent p-0 shadow-none",
    header: "hidden",
    footer: "hidden",
    main: "gap-6",
    form: "gap-4",
    formFieldLabel: "hidden",
    formFieldInput:
      "h-[52px] rounded-lg border border-[#D6D6D6] bg-white px-[22px] font-medium text-[#2C2C2C] text-base tracking-[-0.24px] placeholder:text-[#7C7C7C] shadow-none focus:border-[#048DDB] focus:ring-[#048DDB]/20",
    formButtonPrimary:
      "h-[50px] w-full rounded-full bg-linear-to-b from-[#02A8EA] to-[#0670CB] font-bold text-base text-white shadow-none hover:opacity-95",
    formFieldAction: "font-medium text-[#048DDB] text-sm hover:underline",
    identityPreview: "rounded-lg border border-[#D6D6D6] bg-white",
    socialButtonsRoot: "gap-[9px]",
    socialButtonsIconButton:
      "h-[50px] flex-1 rounded-full border border-[#7C7C7C] bg-transparent hover:bg-white",
    dividerRow: "gap-3",
    dividerLine: "bg-[#7C7C7C]",
    dividerText: "font-medium text-[#7C7C7C] text-base",
    alternativeMethodsBlockButton:
      "rounded-lg border border-[#D6D6D6] bg-white text-[#2C2C2C]",
  },
};

export function HomeV2Login({ initialMode = "sign-in" }: HomeV2LoginProps) {
  const router = useRouter();
  const { isSignedIn } = useUser();
  const [role, setRole] = useState<Role>("diyer");
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);

  useEffect(() => {
    if (isSignedIn) {
      router.replace("/");
    }
  }, [isSignedIn, router]);

  useEffect(() => {
    const id = window.setInterval(() => {
      setSlide((current) => (current + 1) % LOGIN_SLIDES.length);
    }, 6000);
    return () => window.clearInterval(id);
  }, []);

  function switchMode(next: AuthMode) {
    setMode(next);
    router.replace(
      next === "sign-up" ? "/v2/login?mode=sign-up" : "/v2/login",
      { scroll: false }
    );
  }

  const activeSlide = LOGIN_SLIDES[slide] ?? LOGIN_SLIDES[0];

  return (
    <div className="relative flex min-h-screen w-full bg-white">
      {/* Left: photo carousel — Figma ~1200 of 1920 */}
      <div className="relative hidden min-h-screen flex-[1.67] overflow-hidden lg:block">
        {LOGIN_SLIDES.map((item, index) => (
          <div
            className={cn(
              "absolute inset-0 transition-opacity duration-700",
              index === slide ? "opacity-100" : "opacity-0"
            )}
            key={item.id}
          >
            <Image
              alt={item.imageAlt}
              className="object-cover object-center"
              fill
              priority={index === 0}
              sizes="65vw"
              src={item.image}
            />
          </div>
        ))}

        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-[52%]"
          style={{
            background:
              "linear-gradient(180deg, rgba(0, 182, 255, 0) 11.49%, rgba(0, 83, 123, 0.64) 55.41%, #001B32 100%)",
          }}
        />

        <div className="absolute inset-x-0 bottom-16 z-10 flex flex-col items-center gap-10 px-12">
          <div className="flex max-w-[621px] flex-col items-center gap-6 text-center">
            <h2 className="font-bold text-[36px] text-tt-yellow leading-[42px]">
              {activeSlide.headline}
            </h2>
            <p className="font-medium text-white/85 text-xl leading-[23px]">
              {activeSlide.description}
            </p>
          </div>

          <div className="flex items-center gap-[7px]">
            {LOGIN_SLIDES.map((item, index) => (
              <button
                aria-label={`Go to slide ${index + 1}`}
                className={cn(
                  "size-3 rounded-full transition-colors",
                  index === slide
                    ? "bg-white"
                    : "border border-white bg-transparent opacity-50"
                )}
                key={item.id}
                onClick={() => setSlide(index)}
                type="button"
              />
            ))}
          </div>
        </div>
      </div>

      {/* Right: form panel — Figma ~720 #F5F5F5 */}
      <div className="flex min-h-screen w-full flex-col items-center justify-center bg-[#F5F5F5] px-6 py-12 lg:w-[720px] lg:flex-none lg:shrink-0">
        <div className="flex w-full max-w-[371px] flex-col items-center gap-12">
          <Link aria-label="TeleTradie home" href="/v2">
            <Logo className="h-[54px] w-[189px]" />
          </Link>

          <div className="flex w-full flex-col items-center gap-10">
            <div className="flex w-full flex-col items-center gap-4 text-center">
              <h1 className="font-bold text-[#2C2C2C] text-[40px] leading-[47px]">
                {mode === "sign-in" ? "Log in" : "Sign up"}
              </h1>
              <p className="font-medium text-[#7C7C7C] text-base leading-[19px]">
                Get access to on-demand live, video advice from trusted and
                qualified tradies on your device.
              </p>
            </div>

            <div className="flex w-full max-w-[351px] flex-col items-center gap-8">
              {/* DIY’er / Tradie toggle — visual preference for this session */}
              <div
                aria-label="Account type"
                className="relative flex h-10 w-full max-w-[350px] items-center rounded-full bg-[#048DDB] p-1"
                role="tablist"
              >
                <span
                  aria-hidden
                  className={cn(
                    "absolute top-1 h-8 w-[calc(50%-4px)] rounded-full bg-white transition-transform duration-200",
                    role === "tradie"
                      ? "translate-x-[calc(100%+4px)]"
                      : "translate-x-0"
                  )}
                />
                <button
                  aria-selected={role === "diyer"}
                  className={cn(
                    "relative z-10 flex-1 font-bold text-xl leading-[23px] transition-colors",
                    role === "diyer" ? "text-[#2C2C2C]" : "text-white/40"
                  )}
                  onClick={() => setRole("diyer")}
                  role="tab"
                  type="button"
                >
                  DIY&apos;er
                </button>
                <button
                  aria-selected={role === "tradie"}
                  className={cn(
                    "relative z-10 flex-1 font-bold text-xl leading-[23px] transition-colors",
                    role === "tradie" ? "text-[#2C2C2C]" : "text-white/40"
                  )}
                  onClick={() => setRole("tradie")}
                  role="tab"
                  type="button"
                >
                  Tradie
                </button>
              </div>

              <div className="w-full">
                {mode === "sign-in" ? (
                  <SignIn
                    appearance={clerkAppearance}
                    fallbackRedirectUrl="/"
                    forceRedirectUrl="/"
                    routing="hash"
                  />
                ) : (
                  <SignUp
                    appearance={clerkAppearance}
                    fallbackRedirectUrl="/"
                    forceRedirectUrl="/"
                    routing="hash"
                  />
                )}
              </div>

              <p className="text-center font-normal text-[#2C2C2C] text-base leading-[19px]">
                {mode === "sign-in" ? (
                  <>
                    Don&apos;t have an account yet?{" "}
                    <button
                      className="font-medium text-[#048DDB] hover:underline"
                      onClick={() => switchMode("sign-up")}
                      type="button"
                    >
                      Sign up here.
                    </button>
                  </>
                ) : (
                  <>
                    Already have an account?{" "}
                    <button
                      className="font-medium text-[#048DDB] hover:underline"
                      onClick={() => switchMode("sign-in")}
                      type="button"
                    >
                      Log in here.
                    </button>
                  </>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
